// src/pages/OAuthCallbackPage.tsx
//
// Handles the Bearer-mode OIDC authorization-code callback. The browser
// arrives here from Central with `?code=...&state=...` (success) or
// `?error=...&error_description=...` (Central-side rejection); this page
// validates the URL state against the localStorage state stored at
// /auth/start time, exchanges the code for tokens via PKCE, stores
// credentials, and navigates to the user's return path.
//
// RP/BFF MODE
// -----------
// When `HANDOFF_OIDC_CRM_ENABLED=true` (staging, production), `/auth/callback`
// is owned by the BACKEND — nginx (`infra/templates/nginx.conf` `location
// /auth/`) proxies it to Express, which mints `crm_sid` server-side. This
// React Router route still exists for the BEARER mode (dev / un-flipped
// envs), AND will catch client-side navigations to `/auth/callback` (e.g. a
// back-button after a successful RP login lands the SPA at this URL via
// History API, bypassing nginx). In RP/BFF mode this page MUST NOT run any
// state-validation against localStorage — RP-mode never stored state there,
// it stored a `crm_oidc_txn` cookie that lives server-side. The page would
// otherwise fail every validation and strand the user.
//
// NEVER STRAND THE USER
// ---------------------
// Prior behavior dead-ended the user on a hard "Invalid state parameter —
// possible CSRF attack" panel for any of:
//   - state mismatch (back-button, multi-tab race, missing localStorage)
//   - Central returned `?error=...`
//   - missing PKCE verifier
// All of these are now recoverable: the page clears any stale localStorage
// and navigates to the canonical recovery surface (RP mode) or the login
// page (Bearer mode) using `replace` so the back button doesn't replay the
// failed callback URL. Security is preserved — the invalid callback is
// NEVER accepted; the user must complete a fresh /auth/start.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/store';
import { setCentralCredentials, selectIsCentralAuthenticated } from '../store/slices/centralAuthSlice';
import { exchangeCodeForTokens } from '../api/centralAuthApi';
import {
  APP_ID,
  HANDOFF_OIDC_CRM_ENABLED,
  PKCE_VERIFIER_KEY,
  OAUTH_STATE_KEY,
  OAUTH_RETURN_PATH_KEY,
} from '../config/centralAuth';
import LoadingSpinner from '../components/common/LoadingSpinner';

/**
 * Where to send the user when this callback cannot complete. In RP/BFF mode
 * we deliver them to the canonical recovery surface (`RpBootGate` consumes
 * `?auth_recovery=` and renders `<RecoverySurface action="signin">`). In
 * Bearer mode we send them to the login page — there is no `RpBootGate`
 * mounted, and `BootGate`/`RequireAuth` already gate the rest of the app.
 *
 * `require_reauth` is the canonical recovery state for "start a fresh sign-in
 * with the same UX as a server-side callback failure" — exported from
 * `server/src/auth/oidc/recovery/index.ts` and consumed by `RpBootGate`.
 */
function recoveryRedirectPath(): string {
  return HANDOFF_OIDC_CRM_ENABLED ? '/?auth_recovery=require_reauth' : '/login';
}

/**
 * Remove any stale Bearer-mode state from `localStorage`. Called on every
 * failure path so a retry produces a FRESH state at the next /auth/start
 * (rather than re-using the leaked-into-localStorage value that just failed
 * validation). No-op when keys are already absent.
 */
function clearBearerCallbackState(): void {
  try {
    localStorage.removeItem(PKCE_VERIFIER_KEY);
    localStorage.removeItem(OAUTH_STATE_KEY);
    localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
  } catch {
    // Ignore — localStorage absent in some test/embed contexts; this is
    // best-effort hygiene only.
  }
}

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAlreadyAuthenticated = useAppSelector(selectIsCentralAuthenticated);
  // `recovering` is purely cosmetic — we show the standard loading spinner
  // until the redirect actually fires. We never render the prior "Login
  // Failed" / CSRF dead-end panel.
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    async function handleCallback() {
      console.log('[OAuthCallback] Starting callback handler');
      console.log('[OAuthCallback] URL:', window.location.href);

      // ── RP/BFF MODE: this page should not run. ─────────────────────────
      // The backend owns /auth/callback in RP/BFF mode (`crm_sid` cookie,
      // server-side txn store). Reaching this client-side handler means a
      // client-routed navigation (back-button, History API push) caught the
      // route. Don't validate Bearer-mode state — there isn't any. Delegate
      // to `RpBootGate` via the recovery surface; if the user actually has
      // a valid `crm_sid`, `/auth/context` will succeed and the recovery
      // param is benign (RpBootGate just strips it).
      if (HANDOFF_OIDC_CRM_ENABLED) {
        console.log('[OAuthCallback] RP/BFF mode active — delegating to /; never running Bearer state-check');
        setRecovering(true);
        navigate(recoveryRedirectPath(), { replace: true });
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      console.log('[OAuthCallback] Params:', {
        code: code?.slice(0, 10) + '...',
        state: state?.slice(0, 10) + '...',
        error: errorParam,
      });

      // ── Central returned an error (e.g. ?error=invalid_state) ──────────
      // Treat as recoverable: clear stale state, redirect to recovery
      // surface. Do NOT render a dead-end panel that strands the user with
      // no fresh-start affordance.
      if (errorParam) {
        console.warn('[OAuthCallback] Central returned error — recovering:', errorParam, errorDescription);
        clearBearerCallbackState();
        setRecovering(true);
        navigate(recoveryRedirectPath(), { replace: true });
        return;
      }

      // ── Missing authorization code ─────────────────────────────────────
      if (!code) {
        console.warn('[OAuthCallback] No authorization code — recovering');
        clearBearerCallbackState();
        setRecovering(true);
        navigate(recoveryRedirectPath(), { replace: true });
        return;
      }

      // ── State mismatch (CSRF guard) ────────────────────────────────────
      // This is the most common failure mode for back-button replays and
      // multi-tab races. Prior behavior rendered a hard "Invalid state
      // parameter — possible CSRF attack" panel; now we recover instead.
      // Security is preserved: we DO NOT exchange the suspect code; we
      // clear any leaked-into-localStorage state so a retry produces a
      // fresh state value at the next /auth/start.
      const savedState = localStorage.getItem(OAUTH_STATE_KEY);
      console.log('[OAuthCallback] State check:', {
        received: state?.slice(0, 10),
        saved: savedState?.slice(0, 10),
      });
      if (!state || state !== savedState) {
        // If the user has a valid Central session (Bearer Redux state), the
        // mismatch was almost certainly a stale-callback replay AFTER a
        // successful login completed in another tab/window. Skip the
        // recovery surface and deliver them straight to the app.
        if (isAlreadyAuthenticated) {
          console.log('[OAuthCallback] State mismatch but already authenticated — redirecting to /');
          navigate('/', { replace: true });
          return;
        }
        console.warn('[OAuthCallback] State mismatch — recovering (no exchange attempted)');
        clearBearerCallbackState();
        setRecovering(true);
        navigate(recoveryRedirectPath(), { replace: true });
        return;
      }

      // ── Missing PKCE code verifier ─────────────────────────────────────
      // The verifier should be in localStorage from the matching
      // /auth/start. Absence means storage was cleared between start and
      // callback (private-mode tab close, manual clear, etc.). Recoverable.
      const codeVerifier = localStorage.getItem(PKCE_VERIFIER_KEY);
      console.log('[OAuthCallback] PKCE verifier present:', !!codeVerifier);
      if (!codeVerifier) {
        if (isAlreadyAuthenticated) {
          console.log('[OAuthCallback] Missing verifier but already authenticated — redirecting to /');
          navigate('/', { replace: true });
          return;
        }
        console.warn('[OAuthCallback] Missing PKCE verifier — recovering');
        clearBearerCallbackState();
        setRecovering(true);
        navigate(recoveryRedirectPath(), { replace: true });
        return;
      }

      // ── Happy path: state matches, verifier present, exchange tokens ───
      const returnPath = localStorage.getItem(OAUTH_RETURN_PATH_KEY) || '/';
      clearBearerCallbackState();

      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        console.log('[OAuthCallback] Exchanging code for tokens...', { redirectUri, clientId: APP_ID });
        const result = await exchangeCodeForTokens({
          code,
          codeVerifier,
          clientId: APP_ID,
          redirectUri,
        });

        console.log('[OAuthCallback] Token exchange success:', {
          hasAccessToken: !!result.accessToken,
          hasRefreshToken: !!result.refreshToken,
          hasUser: !!result.user,
          userId: result.user?.id,
          workspaceId: result.user?.selectedWorkspaceId,
        });

        if (!result.user) {
          throw new Error('No user information received');
        }

        dispatch(
          setCentralCredentials({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken || '',
            expiresIn: result.expiresIn,
            user: result.user,
          })
        );

        console.log('[OAuthCallback] Credentials stored, navigating to:', returnPath);
        navigate(returnPath, { replace: true });
      } catch (err) {
        // Token-exchange failure: treat as recoverable (the exchange may
        // have raced with an already-consumed code, expired authorize
        // window, or transient network). Don't dead-end the user.
        console.error('[OAuthCallback] Token exchange failed — recovering:', err);
        setRecovering(true);
        navigate(recoveryRedirectPath(), { replace: true });
      }
    }

    handleCallback();
    // navigate / dispatch / searchParams are stable identities from react-
    // router and react-redux; isAlreadyAuthenticated is the Redux value at
    // mount. We intentionally do not re-run on dispatch change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-500">
        {recovering ? 'Redirecting…' : 'Completing sign in…'}
      </p>
    </div>
  );
}
